/**
 * Aircrafts Registry Store - Manages UI state for the Aircrafts Registry page
 *
 * v3: Contextual columns auto-selected per saved view (no manual column group toggle)
 *
 * Provides:
 * - Active saved view (Ramp, Based Fleet, Transients, All)
 * - Quick filter state (Presence, Relationship, CRM Stage)
 * - Grouping option (By Status, Location, Relationship, Operator, CRM Stage, Type, None)
 * - Search text
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  SavedViewId,
  GroupingOption,
  QuickFilterState,
  PresenceFilter,
  RelationshipFilter,
  CrmStageFilter,
} from '@/types/aircrafts'
import { getSavedViewConfig } from '@/types/aircrafts'
import type { RecordStatusFilter } from '@/types/record-status'

// ============================================
// Types
// ============================================

interface AircraftsRegistryState {
  /** Currently active saved view */
  activeView: SavedViewId
  /** Quick filter state (resets to view defaults on view switch / page load) */
  quickFilters: QuickFilterState
  /** Current grouping option */
  groupBy: GroupingOption
  /** Global search text */
  search: string
  /** Record status filter (Active / All / Deleted) */
  recordStatus: RecordStatusFilter
}

interface AircraftsRegistryActions {
  /** Switch active view — resets filters and grouping to view defaults */
  setActiveView: (viewId: SavedViewId) => void
  /** Set presence filter values */
  setPresenceFilter: (values: PresenceFilter[]) => void
  /** Set relationship filter values */
  setRelationshipFilter: (values: RelationshipFilter[]) => void
  /** Set CRM stage filter values */
  setStageFilter: (values: CrmStageFilter[]) => void
  /** Set location filter values */
  setLocationFilter: (values: string[]) => void
  /** Reset quick filters to active view's defaults */
  resetFiltersToViewDefaults: () => void
  /** Check if current filters differ from active view defaults */
  getIsModified: () => boolean
  /** Set the grouping option */
  setGroupBy: (option: GroupingOption) => void
  /** Set search text */
  setSearch: (search: string) => void
  /** Set record status filter */
  setRecordStatus: (status: RecordStatusFilter) => void
}

// ============================================
// Default values
// ============================================

function getDefaultQuickFilters(viewId: SavedViewId): QuickFilterState {
  const config = getSavedViewConfig(viewId)
  return {
    presence: [...config.defaultFilters.presence],
    relationship: [...config.defaultFilters.relationship],
    stage: [...config.defaultFilters.stage],
    location: [...config.defaultFilters.location],
  }
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((v, i) => v === sortedB[i])
}

// ============================================
// Store
// ============================================

export const useAircraftsRegistryStore = create<AircraftsRegistryState & AircraftsRegistryActions>()(
  devtools(
    persist(
      (set, get) => ({
        activeView: 'ramp',
        quickFilters: getDefaultQuickFilters('ramp'),
        groupBy: 'status',
        search: '',
        recordStatus: 'active',

        setActiveView: (viewId) => {
          const config = getSavedViewConfig(viewId)
          set({
            activeView: viewId,
            quickFilters: getDefaultQuickFilters(viewId),
            groupBy: config.defaultGroupBy,
            search: '',
            recordStatus: 'active',
          })
        },

        setPresenceFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, presence: values },
          })),

        setRelationshipFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, relationship: values },
          })),

        setStageFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, stage: values },
          })),

        setLocationFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, location: values },
          })),

        resetFiltersToViewDefaults: () => {
          const { activeView } = get()
          const config = getSavedViewConfig(activeView)
          set({
            quickFilters: getDefaultQuickFilters(activeView),
            groupBy: config.defaultGroupBy,
          })
        },

        getIsModified: () => {
          const { activeView, quickFilters } = get()
          const defaults = getSavedViewConfig(activeView).defaultFilters
          return (
            !arraysEqual(quickFilters.presence, defaults.presence) ||
            !arraysEqual(quickFilters.relationship, defaults.relationship) ||
            !arraysEqual(quickFilters.stage, defaults.stage) ||
            !arraysEqual(quickFilters.location, defaults.location)
          )
        },

        setGroupBy: (option) => set({ groupBy: option }),

        setSearch: (search) => set({ search }),

        setRecordStatus: (status) => set({ recordStatus: status }),
      }),
      {
        name: 'aircrafts-registry',
        // Only persist activeView and groupBy
        // quickFilters reset to view defaults on page load
        partialize: (state) => ({
          activeView: state.activeView,
          groupBy: state.groupBy,
        }),
        // Custom deserialization with migration from old tab names
        merge: (persisted, current) => {
          const p = persisted as {
            activeView?: SavedViewId
            activeTab?: string // legacy field
            groupBy?: GroupingOption
          }

          // Migrate old activeTab to activeView
          let activeView = p.activeView ?? current.activeView
          if (!p.activeView && p.activeTab) {
            const tabMap: Record<string, SavedViewId> = {
              on_field: 'ramp',
              based: 'based_fleet',
              all_known: 'all',
              network: 'all',
            }
            activeView = tabMap[p.activeTab] ?? 'ramp'
          }

          // Validate activeView
          const validViews: SavedViewId[] = ['ramp', 'based_fleet', 'transients', 'all']
          if (!validViews.includes(activeView)) {
            activeView = 'ramp'
          }

          return {
            ...current,
            activeView,
            groupBy: p.groupBy ?? current.groupBy,
            // Always reset quickFilters to view defaults on load
            quickFilters: getDefaultQuickFilters(activeView),
          }
        },
      }
    ),
    { name: 'aircrafts-registry' }
  )
)
